<template>
  <router-link :to="detailRoute" class="opportunity-card">
    <!-- Header -->
    <div class="card-head">
      <div class="logo">
        <img :src="token.image" :alt="token.name" loading="lazy" @error="onImgError" />
      </div>
      <div class="head-text">
        <p class="name">{{ token.name }}</p>
        <p class="symbol">{{ token.symbol }}</p>
      </div>
      <DealTypeBadge :badge="view.badge" />
    </div>

    <!-- Tag or first badge -->
    <div class="tag-row" v-if="tag || firstBadge || token.sharia_compliant">
      <ShariaBadge :token="token" size="sm" />
      <span v-if="tag" class="tag" :style="{ color: tag.color, background: tint(tag.color) }">
        {{ tag.label }}
      </span>
      <TokenBadge v-else-if="firstBadge" :badge="firstBadge" />
    </div>

    <!-- Hero metric -->
    <div class="hero-metric">
      <template v-if="yieldHero">
        <span class="metric-value">{{ view.hero.value }}</span>
        <span class="metric-label">{{ view.hero.unit }}</span>
      </template>
      <template v-else>
        <div class="price-line">
          <span class="metric-value">{{ view.hero.value }}</span>
          <ChangePill :change="change" />
        </div>
        <span class="metric-label">{{ view.hero.unit }}</span>
      </template>
    </div>

    <div class="divider"></div>

    <!-- Mini rows -->
    <div class="mini-rows">
      <div class="mini-row" v-if="!hideDetails && detail">
        <span class="mini-label">{{ detail.k }}</span>
        <span class="mini-value">{{ detail.v }}</span>
      </div>
      <div class="mini-row">
        <span class="mini-label">Available to buy</span>
        <span class="mini-value">{{ available }}</span>
      </div>
      <div class="mini-row" v-if="!hideDetails">
        <span class="mini-label">Min. purchase</span>
        <span class="mini-value">{{ minBuy }}</span>
      </div>
    </div>

    <div class="card-action">
      <span class="buy-link">
        {{ ctaLabel }}
        <svg viewBox="0 0 16 16" fill="none">
          <path d="M6 12L10 8L6 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </span>
    </div>
  </router-link>
</template>

<script>
import TokenBadge from "./TokenBadge.vue";
import ChangePill from "./ChangePill.vue";
import * as ex from "@/utils/exchange.js";
import DealTypeBadge from "@/components/general/DealTypeBadge.vue";
import ShariaBadge from "@/components/general/ShariaBadge.vue";
import { DealKinds, listingView } from "@/utils/trade-view";

export default {
  name: "OpportunityCard",
  components: { TokenBadge, ChangePill, DealTypeBadge, ShariaBadge },
  props: {
    token: { type: Object, required: true },
    hideDetails: { type: Boolean, default: false },
    tag: { type: Object, default: null }, // { label, color }
  },
  computed: {
    // Badge, headline and pills by kind of deal (utils/trade-view), shared with every list.
    view() {
      return listingView(this.token);
    },
    // A note's yield, or a fund's rate, is the headline; everything else leads with its price.
    yieldHero() {
      return this.view.kind === DealKinds.DEBT || (this.view.kind === DealKinds.FUND && Number(this.token.interest) > 0);
    },
    detail() {
      if (this.view.kind === DealKinds.DEBT) return { k: "Time remaining", v: this.timeRemaining };
      return this.view.pills[0] || null;
    },
    ctaLabel() {
      return [DealKinds.COMMODITY, DealKinds.LISTED].includes(this.view.kind) ? this.view.cta : "Buy Now";
    },
    change() {
      return ex.changePercentage(this.token);
    },
    timeRemaining() {
      const d = ex.daysRemaining(this.token);
      return d > 0 ? `${d} days` : "—";
    },
    // "Available to buy": what the active sell orders are worth, as the mobile app shows it.
    // Not the deal's remaining supply, which once a raise closes is the whole issue whether or
    // not anyone is selling.
    available() {
      return ex.money(ex.sellOrderValue(this.token), ex.tokenCurrency(this.token), 0);
    },
    minBuy() {
      return ex.money(ex.minPurchase(this.token), ex.tokenCurrency(this.token), 0);
    },
    firstBadge() {
      return ex.listingBadges(this.token)[0] || null;
    },
    detailRoute() {
      return {
        name: "TokenOverview",
        params: { symbol: this.token.symbol, id: this.token._id || this.token.id },
      };
    },
  },
  methods: {
    tint(color) {
      const hex = color.replace("#", "");
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, 0.12)`;
    },
    onImgError(e) {
      e.target.src = require("@/assets/img/token-icon.svg");
    },
  },
};
</script>

<style lang="scss" scoped>
.opportunity-card {
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 11px;
  padding: 20px;
  text-decoration: none;
  transition: all 0.2s ease;
  height: 100%;

  &:hover {
    border-color: #4d84ff;
    box-shadow: 0 4px 20px rgba(77, 132, 255, 0.12);
    transform: translateY(-2px);

    .buy-link {
      color: #4d84ff;
    }
  }
}

.card-head {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.logo {
  width: 48px;
  height: 48px;
  border-radius: 4px;
  overflow: hidden;
  background: #f8fafc;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.head-text {
  flex: 1;
  min-width: 0;
}

.name {
  margin: 0 0 2px;
  font-size: 16px;
  font-weight: 500;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.symbol {
  margin: 0;
  font-size: 13px;
  font-weight: 400;
  color: #64748b;
}


.tag-row {
  min-height: 20px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.tag {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 500;
}

.hero-metric {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.price-line {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.metric-value {
  font-size: 22px;
  font-weight: 500;
  line-height: 1.1;
  color: #0f172a;
}

.metric-label {
  font-size: 11px;
  font-weight: 400;
  color: #64748b;
}

.divider {
  height: 1px;
  background: #e2e8f0;
}

.mini-rows {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.mini-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.mini-label {
  font-size: 11px;
  font-weight: 400;
  color: #64748b;
}

.mini-value {
  font-size: 12px;
  font-weight: 500;
  color: #0f172a;
}

.card-action {
  display: flex;
  justify-content: flex-end;
  margin-top: auto;
}

.buy-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
  transition: color 0.2s ease;

  svg {
    width: 16px;
    height: 16px;
  }
}
</style>
